// ═══════════════════════════════════════════════════════════════════
//  TOQUM · CUSTOMER FEEDBACK MANAGEMENT
//  Style / order / shipment / size-wise customer complaints with
//  supporting images, claim calculation, stakeholder mailing with
//  templates, CAP request, and a supplier-wise claim summary that
//  feeds the rating page and the dashboard.
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import {
  T, uid, today, addDays, num, Page, CardRow, Split, Section, Field, Grid, Btn, DataTable,
  TabBar, Tag, Badge, StatCard, DynamicTable, Collapse, Modal, Toast, printHTML,
  uploadFile, apiPost, isManager, hasRight,
} from './Shared';

export const COMPLAINT_TYPES = [
  'Quality Defect','Measurement / Fit','Fabric Fault','Colour / Shade Variation','Print / Embroidery Defect',
  'Wash Defect','Trims Failure','Packing / Labelling Error','Short Shipment','Wrong Goods Shipped',
  'Safety Issue','Late Delivery','Other',
];

export const CLAIM_METHODS = {
  'FOB Value':        { key:'fob',    label:'Affected pcs × FOB price',        needs:['pcs','fob'] },
  'Material Cost':    { key:'mat',    label:'Affected pcs × material cost',    needs:['pcs','materialCost'] },
  'Labour Cost':      { key:'lab',    label:'Affected pcs × labour cost',      needs:['pcs','labourCost'] },
  'Retail Price':     { key:'retail', label:'Affected pcs × retail price',     needs:['pcs','retailPrice'] },
  'FOB + Handling':   { key:'fobh',   label:'(pcs × FOB) + handling charges',  needs:['pcs','fob','handling'] },
  'Discount / Markdown': { key:'disc', label:'pcs × retail × markdown %',      needs:['pcs','retailPrice','markdownPct'] },
  'Lump Sum':         { key:'lump',   label:'Fixed agreed amount',             needs:['lumpSum'] },
};

export const calcClaim = (c) => {
  const pcs = num(c.affectedPcs);
  switch (c.claimMethod) {
    case 'FOB Value':          return pcs * num(c.fob);
    case 'Material Cost':      return pcs * num(c.materialCost);
    case 'Labour Cost':        return pcs * num(c.labourCost);
    case 'Retail Price':       return pcs * num(c.retailPrice);
    case 'FOB + Handling':     return pcs * num(c.fob) + num(c.handling);
    case 'Discount / Markdown':return pcs * num(c.retailPrice) * (num(c.markdownPct)/100);
    case 'Lump Sum':           return num(c.lumpSum);
    default: return 0;
  }
};

const DEFAULT_TEMPLATES = [
  { id:'tpl-claim', name:'Customer Complaint & Claim Notice',
    subject:'Customer Complaint — {style} / PO {po} — Claim {amount}',
    body:`Dear {supplier} team,

We have received a customer complaint against the goods supplied under the details below.

Style          : {style}
PO             : {po}
Shipment       : {shipment}
Affected sizes : {sizes}
Affected qty   : {pcs} pcs
Complaint type : {type}
Received on    : {date}

Customer's description:
{description}

Claim basis    : {method}
Claim value    : {amount}

Please review the attached evidence and revert within 7 working days with:
1. Your root cause analysis
2. A Corrective Action Plan (CAP) with responsible persons and target dates
3. Your position on the claim value stated above

Regards,
{sender}
Quality Assurance`,
  },
  { id:'tpl-cap', name:'CAP Request Only',
    subject:'CAP required — {style} / PO {po}',
    body:`Dear {supplier} team,

A customer complaint has been logged against {style} (PO {po}).

Complaint : {type}
Details   : {description}

Please submit a Corrective Action Plan within 5 working days covering root cause,
corrective action, preventive action and responsible persons.

This complaint is recorded against your quality performance rating.

Regards,
{sender}`,
  },
  { id:'tpl-internal', name:'Internal Stakeholder Summary',
    subject:'[Internal] Customer complaint — {style} / {supplier}',
    body:`Team,

Logging a customer complaint for visibility.

Supplier : {supplier}
Style    : {style}
PO       : {po}
Type     : {type}
Affected : {pcs} pcs
Claim    : {amount} ({method})

Description:
{description}

CAP has been requested from the supplier. Tracking in Toqum.

{sender}`,
  },
];

const fillTemplate = (text, c, sender) => (text||'')
  .replace(/{supplier}/g,    c.supplier || '')
  .replace(/{style}/g,       c.styleNo || '')
  .replace(/{po}/g,          c.poNo || '')
  .replace(/{shipment}/g,    c.shipmentRef || '')
  .replace(/{sizes}/g,       (c.sizeDetails||[]).map(s=>`${s.size} (${s.qty})`).join(', ') || '—')
  .replace(/{pcs}/g,         c.affectedPcs || '0')
  .replace(/{type}/g,        c.complaintType || '')
  .replace(/{date}/g,        c.date || '')
  .replace(/{description}/g, c.description || '')
  .replace(/{method}/g,      c.claimMethod || '')
  .replace(/{amount}/g,      `$${num(c.claimAmount).toLocaleString()}`)
  .replace(/{sender}/g,      sender || '');

export default function CustomerFeedback({ db, setDb, saveToDB, currentUser }) {
  const TABS = ['DASHBOARD','LOG COMPLAINT','COMPLAINT REGISTER','SUPPLIER SUMMARY','TEMPLATES'];
  const [tab, setTab] = useState('DASHBOARD');
  const [detail, setDetail] = useState(null);
  const [toast, setToast] = useState(null);
  const notify = (m,t='info') => { setToast({ msg:m, type:t }); setTimeout(()=>setToast(null), 4500); };

  const complaints = db.customerComplaints || [];
  const pos = db.pos || [];
  const suppliers = (db.suppliers||[]).map(s=>s.name);
  const templates = (db.emailTemplates||[]).length ? db.emailTemplates : DEFAULT_TEMPLATES;
  const canManage = isManager(currentUser) || hasRight(currentUser, 'SUPPLIER', 'approve');

  // ═══ DASHBOARD ═══
  const Dashboard = () => {
    const open = complaints.filter(c=>!['Closed','Settled'].includes(c.status));
    const totalClaim = complaints.reduce((a,c)=>a+num(c.claimAmount),0);
    const settled = complaints.reduce((a,c)=>a+num(c.settledAmount),0);
    const byType = COMPLAINT_TYPES.map(t=>({ type:t,
      count: complaints.filter(c=>c.complaintType===t).length,
      value: complaints.filter(c=>c.complaintType===t).reduce((a,c)=>a+num(c.claimAmount),0),
    })).filter(x=>x.count).sort((a,b)=>b.count-a.count);
    return (
      <div>
        <CardRow min={200}>
          <StatCard label="Total Complaints" value={complaints.length} color={T.primary} />
          <StatCard label="Open" value={open.length} color={open.length?T.warning:T.success} />
          <StatCard label="Total Claim Value" value={`$${totalClaim.toLocaleString()}`} color={T.danger} />
          <StatCard label="Settled" value={`$${settled.toLocaleString()}`} color={T.success} />
          <StatCard label="Balance" value={`$${(totalClaim-settled).toLocaleString()}`} color={T.purple} />
          <StatCard label="Affected Pieces"
            value={complaints.reduce((a,c)=>a+num(c.affectedPcs),0).toLocaleString()} color={T.orange} />
          <StatCard label="CAPs Requested" value={complaints.filter(c=>c.capRequested).length} color={T.teal} />
          <StatCard label="Awaiting Supplier Reply"
            value={complaints.filter(c=>c.status==='CAP Requested').length} color={T.warning} />
        </CardRow>
        <Split min={420}>
          <Section title="Complaints by Type">
            <DataTable columns={['Complaint Type','Count','Claim Value']} rows={byType.map((b,i)=>({...b,id:i}))}
              renderCell={(r,c)=>({ 'Complaint Type':<b>{r.type}</b>, Count:r.count,
                'Claim Value':`$${r.value.toLocaleString()}` }[c])}
              emptyMsg="No complaints logged yet." />
          </Section>
          <Section title="Open Complaints Needing Action">
            <DataTable columns={['Date','Supplier','Style','Type','Claim','Status']} rows={open.slice(0,12)}
              renderCell={(r,c)=>({ Date:r.date, Supplier:r.supplier, Style:r.styleNo, Type:r.complaintType,
                Claim:`$${num(r.claimAmount).toLocaleString()}`, Status:<Badge status={r.status}/> }[c])}
              actions={r => <Btn small label="Open" onClick={()=>{ setDetail(r); setTab('COMPLAINT REGISTER'); }} />}
              emptyMsg="Nothing open." />
          </Section>
        </Split>
      </div>
    );
  };

  // ═══ LOG COMPLAINT ═══
  const LogComplaint = ({ existing, onDone }) => {
    const [f, setF] = useState(existing || {
      date: today(), receivedFrom:'', customerRef:'', poNo:'', styleNo:'', supplier:'', brand:'',
      shipmentRef:'', shipmentDate:'', complaintType:'Quality Defect', severity:'Major',
      description:'', sizeDetails:[], affectedPcs:'', totalShipped:'',
      claimMethod:'FOB Value', fob:'', materialCost:'', labourCost:'', retailPrice:'',
      handling:'', markdownPct:'', lumpSum:'', claimAmount:0, images:[], documents:[],
      status:'Logged', capRequested:false, loggedBy: currentUser.name,
    });
    const set = (k,v) => setF(p=>{
      const next = { ...p, [k]:v };
      next.claimAmount = calcClaim(next);
      return next;
    });
    const po = pos.find(p=>p.poNo===f.poNo);

    // Pull style/supplier from the PO automatically
    const pickPO = (poNo) => {
      const p = pos.find(x=>x.poNo===poNo);
      setF(prev => ({ ...prev, poNo, styleNo:p?.styleNo||prev.styleNo,
        supplier:p?.supplier||prev.supplier, brand:p?.brand||prev.brand }));
    };

    const addFiles = async (e, kind) => {
      const files = Array.from(e.target.files||[]); e.target.value='';
      for (const file of files) {
        const up = await uploadFile(file, { category:'complaint', poNo:f.poNo, styleNo:f.styleNo,
          supplier:f.supplier, docType: kind==='images'?'Complaint Image':'Complaint Document',
          uploadedBy: currentUser.name });
        setF(p=>({ ...p, [kind]:[...p[kind], { name:up.name, url:up.url, caption:'', offline:up.offline }] }));
      }
      notify(`${files.length} file(s) attached.`, 'success');
    };

    const totalSizeQty = (f.sizeDetails||[]).reduce((a,s)=>a+num(s.qty),0);
    const affectedPct = num(f.totalShipped) ? (num(f.affectedPcs)/num(f.totalShipped)*100).toFixed(2) : null;

    const save = async () => {
      if (!f.supplier || !f.complaintType) return notify('Supplier and complaint type are required', 'error');
      if (!f.description) return notify('A description of the complaint is required', 'error');
      const entry = { ...f, id: f.id || uid(), claimAmount: calcClaim(f) };
      setDb(p=>({ ...p, customerComplaints:[...complaints.filter(x=>x.id!==entry.id), entry] }));
      await saveToDB('customerComplaint', entry);
      notify('Complaint logged.', 'success');
      onDone ? onDone(entry) : setTab('COMPLAINT REGISTER');
    };

    return (
      <div>
        <Section title="Complaint Details">
          <Grid cols={4}>
            <Field label="Date Received *" type="date" value={f.date} onChange={e=>set('date',e.target.value)} />
            <Field label="Received From (customer / channel)" value={f.receivedFrom} onChange={e=>set('receivedFrom',e.target.value)}
              placeholder="e.g. Store returns, e-commerce" />
            <Field label="Customer Reference" value={f.customerRef} onChange={e=>set('customerRef',e.target.value)} />
            <Field label="PO" value={f.poNo} onChange={e=>pickPO(e.target.value)} options={pos.map(p=>p.poNo)} />
            <Field label="Style" value={f.styleNo} onChange={e=>set('styleNo',e.target.value)} />
            <Field label="Supplier *" value={f.supplier} onChange={e=>set('supplier',e.target.value)} options={suppliers} />
            <Field label="Brand" value={f.brand} onChange={e=>set('brand',e.target.value)}
              options={(db.brands||[]).map(b=>b.name||b)} />
            <Field label="Shipment Reference" value={f.shipmentRef} onChange={e=>set('shipmentRef',e.target.value)}
              placeholder="invoice / container / AWB" />
            <Field label="Shipment Date" type="date" value={f.shipmentDate} onChange={e=>set('shipmentDate',e.target.value)} />
            <Field label="Complaint Type *" value={f.complaintType} onChange={e=>set('complaintType',e.target.value)}
              options={COMPLAINT_TYPES} />
            <Field label="Severity" value={f.severity} onChange={e=>set('severity',e.target.value)}
              options={['Critical','Major','Minor']} />
            <Field label="Total Shipped Qty" type="number" value={f.totalShipped} onChange={e=>set('totalShipped',e.target.value)} />
          </Grid>
          <Field label="Customer Complaint Description *" type="textarea" value={f.description}
            onChange={e=>set('description',e.target.value)} />
        </Section>

        <Section title={`Size-wise Affected Detail — matrix total ${totalSizeQty.toLocaleString()}${
          f.affectedPcs && totalSizeQty !== num(f.affectedPcs) ? ` ⚠ (affected pcs entered: ${num(f.affectedPcs).toLocaleString()})` : ''}`}>
          <DynamicTable dense
            columns={[
              { label:'Colour', key:'colour' },
              { label:'Size', key:'size' },
              { label:'Affected Qty', key:'qty', type:'number' },
              { label:'Defect Observed', key:'defect', type:'textarea', width:'220px' },
            ]}
            data={f.sizeDetails} setData={v=>set('sizeDetails',v)}
            onAdd={()=>set('sizeDetails',[...f.sizeDetails,{}])} />
          <Grid cols={3}>
            <Field label="Total Affected Pieces *" type="number" value={f.affectedPcs} onChange={e=>set('affectedPcs',e.target.value)} />
            <div style={{ paddingTop:20, fontSize:12, color:T.textSub }}>
              {affectedPct !== null && <>Affected rate: <b style={{ color: affectedPct>5?T.danger:T.warning }}>{affectedPct}%</b> of shipment</>}
            </div>
            <div style={{ paddingTop:16 }}>
              <Btn small outline label="Use matrix total" onClick={()=>set('affectedPcs', String(totalSizeQty))} />
            </div>
          </Grid>
        </Section>

        <Section title="Claim Calculation">
          <Grid cols={4}>
            <Field label="Claim Basis" value={f.claimMethod} onChange={e=>set('claimMethod',e.target.value)}
              options={Object.keys(CLAIM_METHODS)} />
            {(CLAIM_METHODS[f.claimMethod]?.needs||[]).includes('fob') &&
              <Field label="FOB Price (per pc)" type="number" value={f.fob} onChange={e=>set('fob',e.target.value)} />}
            {(CLAIM_METHODS[f.claimMethod]?.needs||[]).includes('materialCost') &&
              <Field label="Material Cost (per pc)" type="number" value={f.materialCost} onChange={e=>set('materialCost',e.target.value)} />}
            {(CLAIM_METHODS[f.claimMethod]?.needs||[]).includes('labourCost') &&
              <Field label="Labour Cost (per pc)" type="number" value={f.labourCost} onChange={e=>set('labourCost',e.target.value)} />}
            {(CLAIM_METHODS[f.claimMethod]?.needs||[]).includes('retailPrice') &&
              <Field label="Retail Price (per pc)" type="number" value={f.retailPrice} onChange={e=>set('retailPrice',e.target.value)} />}
            {(CLAIM_METHODS[f.claimMethod]?.needs||[]).includes('handling') &&
              <Field label="Handling / Freight Charges" type="number" value={f.handling} onChange={e=>set('handling',e.target.value)} />}
            {(CLAIM_METHODS[f.claimMethod]?.needs||[]).includes('markdownPct') &&
              <Field label="Markdown %" type="number" value={f.markdownPct} onChange={e=>set('markdownPct',e.target.value)} />}
            {(CLAIM_METHODS[f.claimMethod]?.needs||[]).includes('lumpSum') &&
              <Field label="Lump Sum Amount" type="number" value={f.lumpSum} onChange={e=>set('lumpSum',e.target.value)} />}
          </Grid>
          <div style={{ background:'#e2445c12', border:`1px solid ${T.danger}`, borderRadius:8,
            padding:'12px 16px', display:'flex', gap:20, alignItems:'center', flexWrap:'wrap' }}>
            <span style={{ fontSize:12, color:T.textSub }}>{CLAIM_METHODS[f.claimMethod]?.label}</span>
            <span style={{ fontSize:20, fontWeight:900, color:T.danger }}>
              ${calcClaim(f).toLocaleString(undefined,{ maximumFractionDigits:2 })}
            </span>
          </div>
        </Section>

        <Section title="Supporting Evidence">
          <div style={{ display:'flex', gap:20, flexWrap:'wrap', marginBottom:10 }}>
            <label style={{ fontSize:12, fontWeight:700, color:T.primary, cursor:'pointer' }}>
              📷 Attach Images ({f.images.length})
              <input type="file" multiple accept="image/*" hidden onChange={e=>addFiles(e,'images')} />
            </label>
            <label style={{ fontSize:12, fontWeight:700, color:T.teal, cursor:'pointer' }}>
              📄 Attach Documents ({f.documents.length})
              <input type="file" multiple hidden onChange={e=>addFiles(e,'documents')} />
            </label>
          </div>
          {f.images.length>0 && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:10 }}>
              {f.images.map((im,i)=>(
                <div key={i} style={{ border:`1px solid ${T.border}`, borderRadius:8, overflow:'hidden' }}>
                  <img src={im.url} alt={im.name} style={{ width:'100%', height:100, objectFit:'cover' }} />
                  <input value={im.caption} placeholder="caption"
                    onChange={e=>set('images', f.images.map((x,ix)=>ix===i?{...x,caption:e.target.value}:x))}
                    style={{ width:'100%', border:'none', borderTop:`1px solid ${T.border}`, padding:5, fontSize:10 }} />
                </div>
              ))}
            </div>
          )}
          {f.documents.length>0 && <div style={{ marginTop:8 }}>
            {f.documents.map((d,i)=><Tag key={i} label={`📄 ${d.name}`} color={T.teal} />)}
          </div>}
        </Section>

        <div style={{ display:'flex', gap:10 }}>
          <Btn label={existing ? 'Update Complaint' : 'Log Complaint'} color={T.success} onClick={save} />
          {onDone && <Btn label="Cancel" color="#64748b" onClick={()=>onDone(null)} />}
        </div>
      </div>
    );
  };

  // ═══ COMPLAINT DETAIL (mailing, CAP, settlement) ═══
  const ComplaintDetail = ({ c }) => {
    const [mailOpen, setMailOpen] = useState(false);
    const [editing, setEditing] = useState(false);
    const [settle, setSettle] = useState({ amount:c.claimAmount, method:'Debit Note' });

    const update = async (patch, event) => {
      const upd = { ...c, ...patch,
        log:[...(c.log||[]), { date:today(), event, by:currentUser.name }] };
      setDb(p=>({ ...p, customerComplaints: complaints.map(x=>x.id===c.id?upd:x) }));
      await saveToDB('customerComplaint', upd);
      setDetail(upd);
    };

    const printComplaint = () => printHTML(`Complaint ${c.styleNo}`, `
      <h1>TOQUM · Customer Complaint Report</h1>
      <table><tr><td><b>Complaint No:</b> CC-${String(c.id).slice(-5)}</td><td><b>Date:</b> ${c.date}</td>
        <td><b>Supplier:</b> ${c.supplier}</td><td><b>Status:</b> ${c.status}</td></tr>
        <tr><td><b>Style:</b> ${c.styleNo||''}</td><td><b>PO:</b> ${c.poNo||''}</td>
        <td><b>Shipment:</b> ${c.shipmentRef||''}</td><td><b>Type:</b> ${c.complaintType}</td></tr>
        <tr><td><b>Affected:</b> ${c.affectedPcs} pcs</td><td><b>Shipped:</b> ${c.totalShipped||''}</td>
        <td><b>Claim basis:</b> ${c.claimMethod}</td><td><b>Claim:</b> $${num(c.claimAmount).toLocaleString()}</td></tr></table>
      <h3>Description</h3><p>${(c.description||'').replace(/\n/g,'<br/>')}</p>
      ${c.sizeDetails?.length?`<h3>Size-wise Detail</h3><table>
        <tr><th>Colour</th><th>Size</th><th>Affected Qty</th><th>Defect</th></tr>
        ${c.sizeDetails.map(s=>`<tr><td>${s.colour||''}</td><td>${s.size||''}</td>
          <td>${s.qty||''}</td><td>${s.defect||''}</td></tr>`).join('')}</table>`:''}
      ${c.images?.length?`<h3>Evidence</h3><div class="imgs">
        ${c.images.map(i=>`<div><img src="${i.url}"/><div style="font-size:8px">${i.caption||i.name}</div></div>`).join('')}</div>`:''}
      <table class="sign"><tr><th>Logged By</th><th>Reviewed By</th><th>Quality Manager</th></tr>
        <tr><td>${c.loggedBy||''}</td><td>${c.reviewedBy||''}</td><td></td></tr></table>`);

    // ── Mail composer ──
    const MailModal = () => {
      const supplierRec = (db.suppliers||[]).find(s=>s.name===c.supplier);
      const supplierEmails = [
        supplierRec?.contact?.email,
        ...(supplierRec?.qaContacts||[]).map(q=>q.email),
      ].filter(Boolean);
      const internalEmails = (db.users||[]).filter(u=>u.email && u.status==='Active')
        .map(u=>({ email:u.email, name:u.name, role:u.role }));

      const [tplId, setTplId] = useState(templates[0].id);
      const [to, setTo]   = useState(supplierEmails);
      const [cc, setCc]   = useState([]);
      const tpl = templates.find(t=>t.id===tplId) || templates[0];
      const [subject, setSubject] = useState(fillTemplate(tpl.subject, c, currentUser.name));
      const [body, setBody]       = useState(fillTemplate(tpl.body, c, currentUser.name));

      const pickTemplate = (id) => {
        const t = templates.find(x=>x.id===id) || templates[0];
        setTplId(id);
        setSubject(fillTemplate(t.subject, c, currentUser.name));
        setBody(fillTemplate(t.body, c, currentUser.name));
      };
      const toggle = (list, setList, email) =>
        setList(list.includes(email) ? list.filter(e=>e!==email) : [...list, email]);

      const send = async () => {
        if (!to.length) return notify('Select at least one recipient', 'error');
        try {
          const res = await apiPost('send_mail.php', { to, cc, subject, body,
            refType:'complaint', refId:c.id, sentBy: currentUser.name });
          if (!res.success) throw new Error(res.error || 'send failed');
          notify(`Mail sent to ${res.recipients} recipient(s).`, 'success');
        } catch (e) {
          notify('Mail server unreachable — logged locally. Copy the text and send manually.', 'warn');
        }
        await update({ status:'CAP Requested', capRequested:true,
          mailsSent:[...(c.mailsSent||[]), { date:today(), to, cc, subject, by:currentUser.name }] },
          `Mail sent to ${to.length} recipient(s) — CAP requested`);
        setMailOpen(false);
      };

      return (
        <Modal title="Send Complaint Summary & CAP Request" width={920} onClose={()=>setMailOpen(false)}
          actions={<>
            <Btn label="Copy Text" color="#64748b"
              onClick={()=>{ navigator.clipboard?.writeText(`${subject}\n\n${body}`);
                notify('Copied to clipboard.', 'success'); }} />
            <Btn label="Send Mail" color={T.success} onClick={send} />
          </>}>
          <Grid cols={2}>
            <Field label="Template" value={tplId} onChange={e=>pickTemplate(e.target.value)}
              options={templates.map(t=>t.id)} />
            <Field label="Subject" value={subject} onChange={e=>setSubject(e.target.value)} />
          </Grid>
          <Split min={300}>
            <div>
              <b style={{ fontSize:11.5 }}>Supplier Recipients</b>
              <div style={{ border:`1px solid ${T.border}`, borderRadius:6, padding:8, maxHeight:120,
                overflow:'auto', marginTop:4 }}>
                {supplierEmails.length ? supplierEmails.map(e=>(
                  <label key={e} style={{ display:'block', fontSize:11.5, padding:'2px 0' }}>
                    <input type="checkbox" checked={to.includes(e)} onChange={()=>toggle(to,setTo,e)} /> {e}
                  </label>
                )) : <span style={{ fontSize:11, color:T.textSub }}>
                  No emails on file for {c.supplier}. Add them in Supplier → Registration → Contacts.</span>}
              </div>
            </div>
            <div>
              <b style={{ fontSize:11.5 }}>Internal Stakeholders (CC)</b>
              <div style={{ border:`1px solid ${T.border}`, borderRadius:6, padding:8, maxHeight:120,
                overflow:'auto', marginTop:4 }}>
                {internalEmails.map(u=>(
                  <label key={u.email} style={{ display:'block', fontSize:11.5, padding:'2px 0' }}>
                    <input type="checkbox" checked={cc.includes(u.email)} onChange={()=>toggle(cc,setCc,u.email)} />
                    {' '}{u.name} <span style={{ color:T.textSub }}>({u.role})</span>
                  </label>
                ))}
              </div>
            </div>
          </Split>
          <Field label="Message" type="textarea" value={body} onChange={e=>setBody(e.target.value)} />
          <textarea value={body} onChange={e=>setBody(e.target.value)} rows={14}
            style={{ width:'100%', padding:10, border:`1px solid ${T.border}`, borderRadius:6,
              fontSize:12, fontFamily:'monospace', boxSizing:'border-box' }} />
        </Modal>
      );
    };

    if (editing) return <LogComplaint existing={c} onDone={x=>{ setEditing(false); if(x) setDetail(x); }} />;

    return (
      <div>
        <Section title={`Complaint CC-${String(c.id).slice(-5)} — ${c.styleNo || c.supplier}`}
          actions={<span style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            <Btn small outline label="✎ Edit" onClick={()=>setEditing(true)} />
            <Btn small outline label="🖨 Print" onClick={printComplaint} />
            <Btn small color="#64748b" label="← Back" onClick={()=>setDetail(null)} />
          </span>}>
          <CardRow min={185} mb={12}>
            <StatCard label="Status" value={c.status} color={T.warning} />
            <StatCard label="Affected" value={`${num(c.affectedPcs).toLocaleString()} pcs`}
              sub={c.totalShipped ? `of ${num(c.totalShipped).toLocaleString()} shipped` : ''} color={T.orange} />
            <StatCard label="Claim Value" value={`$${num(c.claimAmount).toLocaleString()}`}
              sub={c.claimMethod} color={T.danger} />
            <StatCard label="Settled" value={`$${num(c.settledAmount).toLocaleString()}`} color={T.success} />
            <StatCard label="Balance"
              value={`$${(num(c.claimAmount)-num(c.settledAmount)).toLocaleString()}`} color={T.purple} />
          </CardRow>
          <Split min={340}>
            <div style={{ fontSize:12.5, lineHeight:1.8 }}>
              <b>Supplier:</b> {c.supplier}<br/>
              <b>Style / PO:</b> {c.styleNo||'—'} / {c.poNo||'—'}<br/>
              <b>Shipment:</b> {c.shipmentRef||'—'} {c.shipmentDate && `(${c.shipmentDate})`}<br/>
              <b>Type:</b> {c.complaintType} · <b>Severity:</b> {c.severity}<br/>
              <b>Received from:</b> {c.receivedFrom||'—'} · <b>Customer ref:</b> {c.customerRef||'—'}
            </div>
            <div style={{ fontSize:12.5 }}>
              <b>Description</b>
              <div style={{ background:'#f8fafc', padding:10, borderRadius:6, marginTop:4,
                whiteSpace:'pre-wrap' }}>{c.description}</div>
            </div>
          </Split>
          {c.sizeDetails?.length>0 && (
            <div style={{ marginTop:12 }}>
              <b style={{ fontSize:12 }}>Size-wise Affected Detail</b>
              <DataTable columns={['Colour','Size','Affected Qty','Defect Observed']}
                rows={c.sizeDetails.map((s,i)=>({ ...s, id:i }))}
                renderCell={(r,col)=>({ Colour:r.colour, Size:<b>{r.size}</b>,
                  'Affected Qty':r.qty, 'Defect Observed':r.defect }[col])} />
            </div>
          )}
          {c.images?.length>0 && (
            <div style={{ marginTop:12 }}>
              <b style={{ fontSize:12 }}>Evidence</b>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',
                gap:10, marginTop:6 }}>
                {c.images.map((im,i)=>(
                  <div key={i} style={{ border:`1px solid ${T.border}`, borderRadius:8, overflow:'hidden' }}>
                    <img src={im.url} alt={im.name} style={{ width:'100%', height:110, objectFit:'cover' }} />
                    <div style={{ padding:5, fontSize:10, color:T.textSub }}>{im.caption||im.name}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>

        <Section title="Action">
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:14 }}>
            <Btn label="✉ Send Summary & Request CAP" color={T.primary} onClick={()=>setMailOpen(true)} />
            {c.status!=='Closed' && <>
              <Btn label="Mark Under Investigation" outline color={T.warning}
                onClick={()=>update({ status:'Under Investigation', reviewedBy:currentUser.name },
                  'Marked under investigation')} />
              <Btn label="Link to Supplier CAP" outline color={T.purple}
                onClick={()=>update({ status:'CAP Received' }, 'Supplier CAP received')} />
            </>}
          </div>
          {c.status!=='Closed' && (
            <div style={{ border:`1px dashed ${T.border}`, borderRadius:8, padding:12 }}>
              <b style={{ fontSize:12 }}>Settlement</b>
              <Grid cols={4}>
                <Field label="Settled Amount" type="number" value={settle.amount}
                  onChange={e=>setSettle({...settle,amount:e.target.value})} />
                <Field label="Settlement Method" value={settle.method}
                  onChange={e=>setSettle({...settle,method:e.target.value})}
                  options={['Debit Note','Replacement','Discount on Next Order','Cash','Insurance','Written Off','Rejected by Supplier']} />
                <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}>
                  <Btn small color={T.success} label="Settle & Close"
                    onClick={()=>update({ settledAmount:num(settle.amount), settlementMethod:settle.method,
                      status:'Closed', closedOn:today() }, `Settled $${num(settle.amount).toLocaleString()} via ${settle.method}`)} />
                </div>
              </Grid>
            </div>
          )}
          {(c.mailsSent||[]).length>0 && (
            <div style={{ marginTop:12 }}>
              <b style={{ fontSize:12 }}>Mails Sent</b>
              <DataTable columns={['Date','To','Subject','By']} rows={c.mailsSent.map((m,i)=>({ ...m, id:i }))}
                renderCell={(r,col)=>({ Date:r.date, To:(r.to||[]).join(', '), Subject:r.subject, By:r.by }[col])} />
            </div>
          )}
          {(c.log||[]).length>0 && (
            <div style={{ marginTop:12 }}>
              <b style={{ fontSize:12 }}>Timeline</b>
              {c.log.map((l,i)=>(
                <div key={i} style={{ fontSize:11.5, padding:'3px 0 3px 10px',
                  borderLeft:`3px solid ${T.primary}`, marginTop:4 }}>
                  <b>{l.date}</b> — {l.event} <span style={{ color:T.textSub }}>({l.by})</span>
                </div>
              ))}
            </div>
          )}
        </Section>
        {mailOpen && <MailModal />}
      </div>
    );
  };

  // ═══ REGISTER ═══
  const Register = () => {
    if (detail) return <ComplaintDetail c={complaints.find(x=>x.id===detail.id)||detail} />;
    return (
      <Section title="Customer Complaint Register">
        <DataTable columns={['Date','Complaint','Supplier','Style','PO','Shipment','Type','Affected','Claim','Status']}
          rows={complaints.slice().reverse()}
          renderCell={(r,c)=>({
            Date:r.date, Complaint:<b>CC-{String(r.id).slice(-5)}</b>, Supplier:r.supplier,
            Style:r.styleNo||'—', PO:r.poNo||'—', Shipment:r.shipmentRef||'—', Type:r.complaintType,
            Affected:`${num(r.affectedPcs).toLocaleString()} pcs`,
            Claim:<b style={{ color:T.danger }}>${num(r.claimAmount).toLocaleString()}</b>,
            Status:<Badge status={r.status==='Closed'?'Closed':r.status==='Logged'?'Open':'Pending'} />,
          }[c])}
          actions={r => <Btn small outline label="Open" onClick={()=>setDetail(r)} />}
          emptyMsg="No complaints logged." />
      </Section>
    );
  };

  // ═══ SUPPLIER SUMMARY (feeds rating + dashboard) ═══
  const SupplierSummary = () => {
    const rows = suppliers.map(s => {
      const cs = complaints.filter(c=>c.supplier===s);
      const claim = cs.reduce((a,c)=>a+num(c.claimAmount),0);
      const settled = cs.reduce((a,c)=>a+num(c.settledAmount),0);
      const pcs = cs.reduce((a,c)=>a+num(c.affectedPcs),0);
      const types = [...new Set(cs.map(c=>c.complaintType))];
      return { id:s, supplier:s, count:cs.length, pcs, claim, settled, balance:claim-settled,
        open: cs.filter(c=>c.status!=='Closed').length, types:types.join(', ') };
    }).filter(r=>r.count).sort((a,b)=>b.claim-a.claim);

    return (
      <div>
        <div style={{ fontSize:12, color:T.textSub, background:'#f8fafc', padding:'10px 14px',
          borderRadius:8, marginBottom:14 }}>
          These totals appear on the supplier rating page and as a dashboard KPI, so a supplier's
          customer-complaint record carries into their performance score.
        </div>
        <Section title="Supplier-wise Complaint & Claim Summary">
          <DataTable columns={['Supplier','Complaints','Open','Affected Pcs','Claim Value','Settled','Balance','Complaint Types']}
            rows={rows}
            renderCell={(r,c)=>({
              Supplier:<b>{r.supplier}</b>, Complaints:r.count,
              Open: r.open ? <Tag label={String(r.open)} color={T.warning}/> : '—',
              'Affected Pcs':r.pcs.toLocaleString(),
              'Claim Value':`$${r.claim.toLocaleString()}`,
              Settled:`$${r.settled.toLocaleString()}`,
              Balance:<b style={{ color:r.balance>0?T.danger:T.success }}>${r.balance.toLocaleString()}</b>,
              'Complaint Types':<span style={{ fontSize:11 }}>{r.types}</span>,
            }[c])}
            emptyMsg="No complaints recorded against any supplier." />
        </Section>
      </div>
    );
  };

  // ═══ TEMPLATES ═══
  const Templates = () => {
    const [list, setList] = useState(templates);
    const save = async () => {
      setDb(p=>({ ...p, emailTemplates:list }));
      for (const t of list) await saveToDB('emailTemplate', t);
      notify('Templates saved.', 'success');
    };
    return (
      <Section title="Email Templates"
        actions={<span style={{ display:'flex', gap:6 }}>
          <Btn small outline label="+ Add" onClick={()=>setList([...list,
            { id:`tpl-${uid()}`, name:'New template', subject:'', body:'' }])} />
          <Btn small color={T.success} label="Save Templates" onClick={save} />
        </span>}>
        <div style={{ fontSize:11.5, color:T.textSub, marginBottom:10 }}>
          Placeholders replaced when composing: {'{supplier} {style} {po} {shipment} {sizes} {pcs} {type} {date} {description} {method} {amount} {sender}'}
        </div>
        {list.map((t,i)=>(
          <Collapse key={t.id} title={t.name} defaultOpen={i===0}>
            <Grid cols={2}>
              <Field label="Template Name" value={t.name}
                onChange={e=>setList(list.map((x,ix)=>ix===i?{...x,name:e.target.value}:x))} />
              <Field label="Subject" value={t.subject}
                onChange={e=>setList(list.map((x,ix)=>ix===i?{...x,subject:e.target.value}:x))} />
            </Grid>
            <textarea value={t.body} rows={12}
              onChange={e=>setList(list.map((x,ix)=>ix===i?{...x,body:e.target.value}:x))}
              style={{ width:'100%', padding:10, border:`1px solid ${T.border}`, borderRadius:6,
                fontSize:12, fontFamily:'monospace', boxSizing:'border-box' }} />
            <Btn small color={T.danger} outline label="Delete template"
              onClick={()=>setList(list.filter((_,ix)=>ix!==i))} />
          </Collapse>
        ))}
      </Section>
    );
  };

  return (
    <Page title="CUSTOMER FEEDBACK MANAGEMENT"
      subtitle="Complaints · claim calculation · stakeholder mailing · supplier claim summary">
      <TabBar tabs={TABS} active={tab} onChange={t=>{ setTab(t); setDetail(null); }} />
      {tab==='DASHBOARD'          && <Dashboard />}
      {tab==='LOG COMPLAINT'      && <LogComplaint />}
      {tab==='COMPLAINT REGISTER' && <Register />}
      {tab==='SUPPLIER SUMMARY'   && <SupplierSummary />}
      {tab==='TEMPLATES'          && <Templates />}
      <Toast msg={toast?.msg} type={toast?.type} onClose={()=>setToast(null)} />
    </Page>
  );
}
